// The sightseeing camera (V) and the photo album. Like Pokémon Snap: stand anywhere, look around in first
// person, zoom, and take pictures. Photos of people get stars for how big and centered they are; the first
// photo of each townsperson goes in your journal. Photos stay on this device (IndexedDB).
import { FirstPerson, type Billboard, type View } from '../render/fpv.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
export interface Photo { id: string; at: number; blob: Blob; place: string; time: string; subjects: string[]; stars: number }

// ---------- the album, in IndexedDB ----------
const DB = 'tt-photos', STORE = 'photos', MAX_PHOTOS = 120;
function db(): Promise<IDBDatabase> {
  return new Promise((res, rej) => { const r = indexedDB.open(DB, 1); r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'id' }); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}
async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((res, rej) => { const t = d.transaction(STORE, mode), r = fn(t.objectStore(STORE)); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}
export const Album = {
  async all(): Promise<Photo[]> { try { return ((await tx('readonly', s => s.getAll())) as Photo[]).sort((a, b) => b.at - a.at); } catch { return []; } },
  async add(p: Photo): Promise<void> {
    await tx('readwrite', s => s.put(p));
    const all = await Album.all(); for (const old of all.slice(MAX_PHOTOS)) await Album.remove(old.id);
  },
  async remove(id: string): Promise<void> { await tx('readwrite', s => s.delete(id)); },
};

export interface SnapHost {
  start(): { x: number; y: number; dir: number; place: string } | null;
  billboards(fp: FirstPerson): Billboard[];
  view(): View;
  timeLabel(): string;
  shutter(): void;
  photographed(names: string[]): void;
}

export class SnapCamera {
  readonly fp = new FirstPerson();
  active = false;
  private view = $<HTMLCanvasElement>('snapView'); private vctx = this.view.getContext('2d')!;
  private keys = new Set<string>();
  private drag: { x: number; y: number } | null = null;
  private place = ''; private home = { x: 0, y: 0 };
  onToggle: (on: boolean) => void = () => {};

  constructor(private host: SnapHost) {
    $('snapBtn').addEventListener('click', () => this.toggle());
    $('snapExit').addEventListener('click', () => this.toggle(false));
    $('snapShoot').addEventListener('click', () => void this.shoot());
    $('snapIn').addEventListener('click', () => this.zoom(-8));
    $('snapOut').addEventListener('click', () => this.zoom(8));
    $('snapAlbum').addEventListener('click', () => void this.openAlbum());
    $('albumClose').addEventListener('click', () => { $('album').hidden = true; });
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      if (e.code === 'KeyV' && !e.repeat) { e.preventDefault(); this.toggle(); return; }
      if (!this.active) return;
      if (e.code === 'Escape') { e.preventDefault(); if (!$('album').hidden) $('album').hidden = true; else this.toggle(false); return; }
      if ((e.code === 'Space' || e.code === 'Enter') && !e.repeat) { e.preventDefault(); void this.shoot(); return; }
      if (e.code === 'Equal' || e.code === 'NumpadAdd') this.zoom(-8);
      if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.zoom(8);
      this.keys.add(e.code); e.preventDefault();
    }, true);
    addEventListener('keyup', e => this.keys.delete(e.code));
    this.view.addEventListener('pointerdown', e => { this.drag = { x: e.clientX, y: e.clientY }; this.view.setPointerCapture(e.pointerId); });
    this.view.addEventListener('pointermove', e => {
      if (!this.drag) return;
      const k = (this.fp.cam.fov / 62) * 0.006;
      this.fp.cam.yaw -= (e.clientX - this.drag.x) * k; this.fp.cam.pitch = Math.max(-60, Math.min(60, this.fp.cam.pitch + (e.clientY - this.drag.y) * 0.25));
      this.drag = { x: e.clientX, y: e.clientY };
    });
    const up = () => { this.drag = null; };
    this.view.addEventListener('pointerup', up); this.view.addEventListener('pointercancel', up);
    this.view.addEventListener('wheel', e => { e.preventDefault(); this.zoom(Math.sign(e.deltaY) * 5); }, { passive: false });
  }

  toggle(force?: boolean): void {
    const on = force ?? !this.active;
    if (on === this.active) return;
    if (on) {
      const s = this.host.start(); if (!s) return;
      const c = this.fp.cam; c.x = s.x; c.y = s.y; c.pitch = 0; c.fov = 62; c.eye = 22;
      c.yaw = [Math.PI / 2, 0, -Math.PI / 2, Math.PI][s.dir] ?? Math.PI / 2;
      this.place = s.place; this.fp.horizonAt = 0.45; this.home = { x: s.x, y: s.y };
      c.fov = 70; c.x -= Math.cos(c.yaw) * 10; c.y -= Math.sin(c.yaw) * 10;   // a small step back, so you're not nose to nose with whoever is next to you
    }
    this.active = on; $('snap').hidden = !on; this.keys.clear();
    this.onToggle(on);
  }

  private zoom(d: number): void { this.fp.cam.fov = Math.max(22, Math.min(80, this.fp.cam.fov + d)); $('snapZoom').textContent = `${Math.round(70 / this.fp.cam.fov * 10) / 10}×`; }

  /** Called every frame while active. */
  frame(dt: number): void {
    const c = this.fp.cam, k = this.keys, turn = 1.6 * dt * (c.fov / 62);
    if (k.has('ArrowLeft') || k.has('KeyA')) c.yaw -= turn;
    if (k.has('ArrowRight') || k.has('KeyD')) c.yaw += turn;
    // step a little forward or back (up to 80 pixels from where you stood), tilt with Page Up/Down or by dragging
    const step = (k.has('ArrowUp') || k.has('KeyW') ? 1 : 0) - (k.has('ArrowDown') || k.has('KeyS') ? 1 : 0);
    if (step) {
      const nx = c.x + Math.cos(c.yaw) * step * 50 * dt, ny = c.y + Math.sin(c.yaw) * step * 50 * dt;
      if (Math.hypot(nx - this.home.x, ny - this.home.y) < 80) { c.x = nx; c.y = ny; }
    }
    if (k.has('PageUp') || k.has('KeyR')) c.pitch = Math.min(60, c.pitch + 60 * dt);
    if (k.has('PageDown') || k.has('KeyF')) c.pitch = Math.max(-60, c.pitch - 60 * dt);
    const w = this.view.clientWidth || innerWidth, h = this.view.clientHeight || innerHeight;
    this.fp.resize(w, h);
    if (this.view.width !== this.fp.W || this.view.height !== this.fp.H) { this.view.width = this.fp.W; this.view.height = this.fp.H; }
    this.fp.render(this.host.view(), this.host.billboards(this.fp));
    this.vctx.drawImage(this.fp.canvas, 0, 0);
    $('snapInfo').textContent = `${this.place} · ${this.host.timeLabel()}`;
  }

  /** Take a picture: score the people in it, frame it like a print, and keep it. */
  private async shoot(): Promise<void> {
    if (!this.active) return;
    this.host.shutter();
    const flash = $('snapFlash'); flash.classList.remove('go'); void flash.offsetWidth; flash.classList.add('go');
    const W = this.fp.W, H = this.fp.H;
    const people = this.fp.shown.filter(s => s.b.kind === 'person' && s.b.name && s.x + s.w / 2 > 0 && s.x + s.w / 2 < W && s.h > H * 0.06);
    let best = 0;
    for (const s of people) { const size = Math.min(1, s.h / (H * 0.7)), center = 1 - Math.min(1, Math.abs(s.x + s.w / 2 - W / 2) / (W / 2)); best = Math.max(best, size * 0.75 + center * 0.25 * size * 2); }
    const stars = !people.length ? 0 : best > 0.55 ? 3 : best > 0.3 ? 2 : 1;
    const names = [...new Set(people.sort((a, b) => b.h - a.h).map(s => s.b.name!))].slice(0, 6);
    // the print: three times the size, a cream border and a caption
    const S = 3, pad = 12, cap = 34, out = document.createElement('canvas');
    out.width = W * S + pad * 2; out.height = H * S + pad + cap;
    const g = out.getContext('2d')!; g.imageSmoothingEnabled = false;
    g.fillStyle = '#fffaf0'; g.fillRect(0, 0, out.width, out.height);
    g.drawImage(this.fp.canvas, pad, pad, W * S, H * S);
    g.fillStyle = '#2f2a26'; g.font = '600 14px Nunito, system-ui, sans-serif';
    g.fillText(`${this.place} · ${this.host.timeLabel()}${names.length ? ' · ' + names.slice(0, 3).join(', ') : ''}`, pad, out.height - 12);
    if (stars) { g.fillStyle = '#d79b1f'; g.font = '700 16px system-ui'; g.fillText('★'.repeat(stars) + '☆'.repeat(3 - stars), out.width - pad - 54, out.height - 11); }
    const blob = await new Promise<Blob | null>(r => out.toBlob(r, 'image/png'));
    if (!blob) return;
    await Album.add({ id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6), at: Date.now(), blob, place: this.place, time: this.host.timeLabel(), subjects: names, stars }).catch(() => {});
    const toast = $('snapToast');
    toast.textContent = stars ? `${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}  ${names[0]}${names.length > 1 ? ` +${names.length - 1}` : ''}` : 'A lovely view.';
    toast.classList.remove('go'); void toast.offsetWidth; toast.classList.add('go');
    if (names.length) this.host.photographed(names);
  }

  async openAlbum(): Promise<void> {
    const box = $('albumGrid'), photos = await Album.all();
    $('album').hidden = false; box.replaceChildren();
    $('albumCount').textContent = `${photos.length} photo${photos.length === 1 ? '' : 's'}`;
    if (!photos.length) { const p = document.createElement('p'); p.className = 'empty'; p.textContent = 'No photos yet. Press Snap!'; box.append(p); return; }
    for (const p of photos) {
      const url = URL.createObjectURL(p.blob), card = document.createElement('figure'); card.className = 'ph';
      const img = document.createElement('img'); img.src = url; img.alt = `${p.place}, ${p.time}${p.subjects.length ? ', ' + p.subjects.join(', ') : ''}`;
      const cap = document.createElement('figcaption'); cap.textContent = (p.stars ? '★'.repeat(p.stars) + ' ' : '') + (p.subjects[0] ?? p.place);
      const acts = document.createElement('div'); acts.className = 'ph-acts';
      const dl = document.createElement('a'); dl.href = url; dl.download = `tiny-town-${new Date(p.at).toISOString().slice(0, 16).replace(/[:T]/g, '-')}.png`; dl.textContent = 'Save'; dl.className = 'pf-mini';
      const del = document.createElement('button'); del.type = 'button'; del.className = 'pf-mini'; del.textContent = 'Delete';
      del.addEventListener('click', async () => { if (!del.dataset.sure) { del.dataset.sure = '1'; del.textContent = 'Sure?'; return; } await Album.remove(p.id); void this.openAlbum(); });
      img.addEventListener('click', () => { const v = $<HTMLImageElement>('albumBig'); v.src = url; v.alt = img.alt; $('albumView').hidden = false; });
      acts.append(dl, del); card.append(img, cap, acts); box.append(card);
    }
    $('albumView').onclick = () => { $('albumView').hidden = true; };
  }
}
